import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type ApiError, type LegalAudience, type LegalDocument, type LegalDocumentKind } from '@vello/api-client';
import { maySelfApprove, fourEyesCountries, formatMarketInstant } from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { PersonName } from '../components/person-name';
import { IdRef } from '../components/code-ref';
import { DrawerStack } from '../components/drawer-stack';
import { useScopeZone } from '../components/scope-zone';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyResult, Field, InlineRow, KeyValueList, ListTable, Notice, PageHeader, SegmentedControl, TextArea, TextInput, Stack, Text, type Column } from '@vello/web-theme';

/**
 * Configurações › Documentos legais (W8-RIDER-SAFETY-CONSENT, D440; finding X-02, owner decision D435 §5 #8).
 *
 * - Per country (the countries of the registered markets): every version of the terms of use and the privacy policy,
 *   for riders and for drivers — draft, published or replaced — with who wrote it and who published it.
 * - A row opens the document's RIGHT-SIDE DRAWER (text preview, state, who wrote it and who published it, when, the four-eyes
 *   state) - nothing is drawn below the list. "Nova versão" (the screen's ONE primary action) and "Editar rascunho" open the
 *   editor in that same drawer, with save / cancel in its footer; a DRAFT can be edited (the editor becomes its
 *   writer) and published by ANOTHER operator — the server refuses the writer (`LEGAL_FOUR_EYES_SELF_PUBLISH`), and so
 *   does the database. Publishing sits behind an in-app confirmation (never a native dialog) that says what it causes:
 *   every rider/driver of that country must accept the new version before using the app again.
 * - A published text is frozen: to change it, write a new version. The server authorizes every call (`legal_document:*` at the
 *   country).
 */
const KINDS: readonly LegalDocumentKind[] = ['TERMS_OF_USE', 'PRIVACY_POLICY'];
const AUDIENCES: readonly LegalAudience[] = ['RIDER', 'DRIVER'];

interface DraftForm {
  readonly kind: LegalDocumentKind;
  readonly audience: LegalAudience;
  readonly version: string;
  readonly locale: string;
  readonly title: string;
  readonly body: string;
}

const EMPTY_FORM: DraftForm = { kind: 'TERMS_OF_USE', audience: 'RIDER', version: '1.0', locale: '', title: '', body: '' };

export function LegalDocumentsScreen(): ReactElement {
  const { runtime, markets, scope, me, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t } = useT();
  const { zone, lang } = useScopeZone();
  const { v, fail, explain } = useVocab();
  const { confirm } = useConsole();
  const countries = fourEyesCountries(markets);
  // D580: the top-bar scope fixes the country; the picker exists only where the scope spans countries (a platform-level operator).
  const [pickedCountry, setCountry] = useState<string>(countries[0] ?? '');
  const country = scope.current.country ?? pickedCountry;
  const [docs, setDocs] = useState<readonly LegalDocument[] | undefined>(undefined);
  /** A document id, `'new'` for the create form, or undefined (the drawer is closed). */
  const [selected, setSelected] = useState<string | undefined>(undefined);
  /** What the drawer shows: the document's detail, or its editor (always the editor for `'new'`). */
  const [mode, setMode] = useState<'detail' | 'edit'>('detail');
  const [form, setForm] = useState<DraftForm>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);

  const refusal = (e: ApiError): string => explain(e) ?? t('legal.reason.other', { code: fail(e) });

  const load = useCallback(async (): Promise<void> => {
    if (country === '') return;
    const res = await runtime.legal.list(country);
    // Review W8S-04: a failed read is an error, never an empty country (an unavailable source is never a zero).
    setDocs(res.ok ? res.value.documents : undefined);
    if (!res.ok) setMessage({ tone: 'danger', text: refusal(res.error) });
  }, [country, runtime]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  const current = selected !== undefined && selected !== 'new' ? docs?.find((d) => d.documentId === selected) : undefined;

  const open = (d: LegalDocument): void => {
    setSelected(d.documentId);
    setMode('detail');
    setForm({ kind: d.kind, audience: d.audience, version: d.version, locale: d.locale, title: d.title, body: d.body });
    setMessage(undefined);
  };

  const startNew = (): void => {
    setSelected('new');
    setMode('edit');
    setForm(EMPTY_FORM);
    setMessage(undefined);
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setMessage(undefined);
    const res = selected === 'new'
      ? await runtime.legal.createDraft({ country, ...form })
      : current !== undefined ? await runtime.legal.editDraft(current.documentId, { title: form.title, body: form.body, locale: form.locale }) : undefined;
    setBusy(false);
    if (res === undefined) return;
    if (!res.ok) {
      setMessage({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    setMessage({ tone: 'success', text: t('legal.saved') });
    setSelected(res.value.documentId);
    setMode('detail');
    await load();
  };

  /** Publishing is irreversible and four-eyes: the shared in-app confirmation says what it causes, then publishes. */
  const askPublish = async (): Promise<void> => {
    if (current === undefined) return;
    const answer = await confirm({
      title: t('legal.publish'),
      body: t('legal.publish.confirm', { audience: v('broadcast.audience', current.audience).toLowerCase() }),
      confirmLabel: t('legal.publish.yes'),
      cancelLabel: t('legal.cancel'),
      tone: 'danger',
    });
    if (answer.confirmed) await publish(current);
  };

  const publish = async (current: LegalDocument): Promise<void> => {
    setBusy(true);
    setMessage(undefined);
    // Review W8S-06: publish exactly the words on screen (their hash); an edit by the writer since then is refused.
    const res = await runtime.legal.publish(current.documentId, current.contentHash);
    setBusy(false);
    if (!res.ok) {
      setMessage({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    setMessage({ tone: 'success', text: t('legal.published') });
    await load();
  };

  const statusTone = (s: LegalDocument['status']): 'neutral' | 'success' | 'warning' => (s === 'PUBLISHED' ? 'success' : s === 'DRAFT' ? 'warning' : 'neutral');
  const editable = mode === 'edit' && (selected === 'new' || current?.status === 'DRAFT');
  const close = (): void => {
    setSelected(undefined);
    setMessage(undefined);
  };
  /** Leaving the editor: a new version closes the drawer; an existing draft goes back to its detail, with the saved words. */
  const cancelEdit = (): void => {
    if (current === undefined) {
      close();
      return;
    }
    setForm({ kind: current.kind, audience: current.audience, version: current.version, locale: current.locale, title: current.title, body: current.body });
    setMode('detail');
    setMessage(undefined);
  };
  const rowTitle = (d: LegalDocument): string => t('legal.row', { kind: v('legal.document_kind', d.kind), audience: v('broadcast.audience', d.audience), version: d.version });
  const when = (instant: string | undefined): string => formatMarketInstant(instant, zone, lang);

  const columns: readonly Column<LegalDocument>[] = [
    {
      key: 'doc',
      header: t('legal.field.kind'),
      width: 'minmax(260px, 2fr)',
      render: (d) => (
        <Stack gap={1}>
          <Text as="strong">{rowTitle(d)}</Text>
          <Text role="caption" tone="tertiary">{d.title}</Text>
        </Stack>
      ),
    },
    { key: 'state', header: t('legal.col.state'), width: 'minmax(120px, .8fr)', render: (d) => <Badge tone={statusTone(d.status)}>{v('privacy.legal_document_status', d.status)}</Badge> },
    { key: 'drafted', header: t('legal.draftedBy'), width: 'minmax(160px, 1fr)', render: (d) => <PersonName id={d.draftedBy} /> },
    { key: 'published', header: t('legal.publishedBy'), width: 'minmax(160px, 1fr)', render: (d) => (d.publishedBy !== undefined ? <PersonName id={d.publishedBy} /> : <Text role="caption" tone="tertiary">—</Text>) },
  ];

  const detail = current;
  const ownDraft = detail !== undefined && me?.id !== undefined && detail.draftedBy.toLowerCase() === me.id.toLowerCase();
  // D868: a super administrator publishes their own draft - neither the four-eyes note nor the "someone else must publish" hint is theirs.

  return (
    <div>
      <PageHeader
        title={t('legal.title')}
        subtitle={t('legal.subtitle')}
        actions={<Button variant="primary" onClick={startNew} disabled={country === ''} data-testid="legal-new">{t('legal.new')}</Button>}
      />
      {scope.current.country === undefined && countries.length > 1 && (
        <SegmentedControl ariaLabel={t('legal.country')} options={countries.map((c) => ({ id: c, label: c }))} value={country} onChange={(c) => { setCountry(c); close(); }} />
      )}
      {message !== undefined && selected === undefined && <Notice tone={message.tone}>{message.text}</Notice>}

      {docs !== undefined && docs.length === 0 && <EmptyResult title={t('legal.empty.title')} detail={t('legal.empty.detail')} />}
      {docs !== undefined && docs.length > 0 && (
        <ListTable
          columns={columns}
          rows={docs}
          rowKey={(d) => d.documentId}
          onRowClick={open}
          {...(selected !== undefined && selected !== 'new' ? { selectedKey: selected } : {})}
          minWidth={760}
        />
      )}

      <Drawer open={selected !== undefined} onClose={close} labelledBy="legal-drawer-title">
        {selected !== undefined && mode === 'edit' && (
          <>
            <DrawerHeader titleId="legal-drawer-title" title={t(selected === 'new' ? 'legal.new' : 'legal.edit')} onClose={close} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
                {message !== undefined && <Notice tone={message.tone}>{message.text}</Notice>}
                {selected === 'new' && (
                  <>
                    <Field label={t('legal.field.kind')} htmlFor="legal-kind">
                      <SegmentedControl ariaLabel={t('legal.field.kind')} options={KINDS.map((k) => ({ id: k, label: v('legal.document_kind', k) }))} value={form.kind} onChange={(k) => setForm({ ...form, kind: k })} />
                    </Field>
                    <Field label={t('legal.field.audience')} htmlFor="legal-audience">
                      <SegmentedControl ariaLabel={t('legal.field.audience')} options={AUDIENCES.map((a) => ({ id: a, label: v('broadcast.audience', a) }))} value={form.audience} onChange={(a) => setForm({ ...form, audience: a })} />
                    </Field>
                    <Field label={t('legal.field.version')} htmlFor="legal-version">
                      <TextInput id="legal-version" className="tnum" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="legal-version" />
                    </Field>
                  </>
                )}
                <Field label={t('legal.field.locale')} htmlFor="legal-locale">
                  <TextInput id="legal-locale" value={form.locale} disabled={!editable} onChange={(e) => setForm({ ...form, locale: e.target.value })} data-testid="legal-locale" />
                </Field>
                <Field label={t('legal.field.title')} htmlFor="legal-title">
                  <TextInput id="legal-title" value={form.title} disabled={!editable} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="legal-title" />
                </Field>
                <Field label={t('legal.field.body')} htmlFor="legal-body">
                  <TextArea id="legal-body" className="vello-ab-bodyarea" value={form.body} disabled={!editable} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="legal-body" />
                </Field>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={cancelEdit} data-testid="legal-cancel">{t('legal.cancel')}</Button>
              {editable && <Button variant="primary" onClick={() => void save()} disabled={busy} data-testid="legal-save">{t(selected === 'new' ? 'legal.create' : 'legal.save')}</Button>}
            </DrawerFooter>
          </>
        )}
        {selected !== undefined && mode === 'detail' && detail !== undefined && (
          <>
            <DrawerHeader titleId="legal-drawer-title" title={rowTitle(detail)} subtitle={detail.title} actions={<Badge tone={statusTone(detail.status)}>{v('privacy.legal_document_status', detail.status)}</Badge>} onClose={close} closeLabel={t('ui.close')} />
            <DrawerBody>
              <DrawerStack>
                {message !== undefined && <Notice tone={message.tone}>{message.text}</Notice>}
                {detail.status !== 'DRAFT' && <Notice tone="neutral">{t('legal.frozen')}</Notice>}
                {detail.status === 'DRAFT' && !(ownDraft && selfApproval) && <Notice tone="neutral">{t(ownDraft ? 'legal.reason.LEGAL_FOUR_EYES_SELF_PUBLISH' : 'legal.fourEyes.note')}</Notice>}
                <KeyValueList
                  items={[
                    { label: t('legal.field.version'), value: detail.version, mono: true },
                    { label: t('legal.field.locale'), value: detail.locale, mono: true },
                    { label: t('legal.draftedBy'), value: <InlineRow gap={2} align="center" wrap><PersonName id={detail.draftedBy} /><Text role="caption" tone="tertiary" className="tnum">{when(detail.draftedAt)}</Text></InlineRow> },
                    ...(detail.publishedBy !== undefined
                      ? [{ label: t('legal.publishedBy'), value: <InlineRow gap={2} align="center" wrap><PersonName id={detail.publishedBy} />{detail.publishedAt !== undefined && <Text role="caption" tone="tertiary" className="tnum">{when(detail.publishedAt)}</Text>}<SelfApprovalNote record={detail} /></InlineRow> }]
                      : []),
                    { label: t('legal.field.hash'), value: <IdRef value={detail.contentHash} copy full /> },
                  ]}
                />
                <Field label={t('legal.field.body')} htmlFor="legal-preview">
                  <TextArea id="legal-preview" className="vello-ab-bodyarea" value={detail.body} readOnly data-testid="legal-preview" />
                </Field>
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={close} data-testid="legal-cancel">{t('ui.close')}</Button>
              {detail.status === 'DRAFT' && <Button variant="secondary" onClick={() => setMode('edit')} disabled={busy} data-testid="legal-edit">{t('legal.edit')}</Button>}
              {detail.status === 'DRAFT' && <Button variant="primary" onClick={() => void askPublish()} disabled={busy} data-testid="legal-publish">{t('legal.publish')}</Button>}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </div>
  );
}
